/**
 * MCERunPanel — Dipali's MCE control panel.
 *
 * Renders a glass-box view of tonight's Member Care Engine sweep:
 *   - Total members
 *   - Per-trigger breakdown (eligible → cooldown → deeply dormant → will push)
 *   - Daily cap + overflow
 *   - Priority lane split
 *   - Last 7 days sent counts
 *   - Cooldown status (quiet window vs eligible vs deeply dormant)
 *   - "Preview Tonight's Run" button → sortable list of every member
 *     about to be drafted, so Dipali can spot-check before the cron fires.
 *
 * All data comes from POST /api/member-care/{client}/dry-run plus
 * GET /api/member-care/{client}/recent-stats?days=7. No live Zoho/Claude
 * calls — pure read-only preview.
 */
import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import {
  Activity, Calendar, RefreshCw, Eye, ChevronDown, ChevronUp, X,
  AlertCircle, Heart, Cake, Gift, Users, Mail, Sparkles,
} from 'lucide-react';

const TRIGGER_META = {
  birthday_self:           { icon: '🎂', label: 'Birthdays (next 7 days)' },
  birthday_spouse:         { icon: '💑', label: 'Spouse birthdays (next 7 days)' },
  birthday_child:          { icon: '👶', label: 'Child birthdays (next 7 days)' },
  anniversary_wedding:     { icon: '💍', label: 'Anniversaries (next 7 days)' },
  dormant_60:              { icon: '💤', label: 'Dormant 60+ days' },
  dormant_90:              { icon: '💤', label: 'Dormant 90+ days' },
  soul_followup_14:        { icon: '📋', label: 'Soul Form follow-up · 14d' },
  soul_followup_30:        { icon: '📋', label: 'Soul Form follow-up · 30d' },
  power_user_no_soul:      { icon: '⚡', label: 'Power users · no Soul Form' },
  new_member_soul_invite:  { icon: '🆕', label: 'New members (last 48h)' },
  festival_greeting:       { icon: '🎉', label: 'Festival greetings' },
  monthly_curated:         { icon: '✉️', label: 'Monthly curated edition' },
  quarterly_review:        { icon: '🗓️', label: 'Quarterly review' },
};

const TRIGGER_GROUPS = {
  Birthdays: ['birthday_self', 'birthday_spouse', 'birthday_child', 'anniversary_wedding'],
  Dormant:   ['dormant_60', 'dormant_90'],
  Soul:      ['soul_followup_14', 'soul_followup_30', 'power_user_no_soul'],
  Onboarding:['new_member_soul_invite'],
  Curated:   ['festival_greeting', 'monthly_curated', 'quarterly_review'],
};

const COLORS = {
  primary:  '#1B2A4A',
  cream:    '#F4F1EB',
  panelBg:  'white',
  panelBorder: '#E5E1DC',
  ink:      '#1B2A4A',
  muted:    '#6B7280',
  gold:     '#C9A66B',
  goldBg:   '#FFFBEB',
  goldBorder: '#FDE68A',
  green:    '#1f5736',
  greenBg:  '#E5F4ED',
  amber:    '#A86B2C',
  amberBg:  '#FFF4E0',
  red:      '#A32D2D',
  redBg:    '#FEE2E2',
  blue:     '#1565C0',
  blueBg:   '#E3F2FD',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function MCERunPanel({ clientId }) {
  const [preview, setPreview] = useState(null);
  const [recent, setRecent]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [showList, setShowList] = useState(false);

  const refresh = async () => {
    setLoading(true); setError(null);
    try {
      const [p, r] = await Promise.all([
        api.post(`/member-care/${clientId}/dry-run`),
        api.get(`/member-care/${clientId}/recent-stats?days=7`),
      ]);
      setPreview(p.data);
      setRecent(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load MCE preview');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientId]);

  if (loading && !preview) {
    return (
      <div className="p-6 text-center text-[12px] rounded border"
            style={{ background: COLORS.panelBg, borderColor: COLORS.panelBorder, color: COLORS.muted }}
            data-testid="mce-runpanel-loading">
        <Activity size={14} className="inline mr-2 animate-pulse" />
        Calculating tonight's sweep…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-[12px] rounded border flex items-start gap-2"
            style={{ background: COLORS.redBg, borderColor: COLORS.red, color: COLORS.red }}
            data-testid="mce-runpanel-error">
        <AlertCircle size={14} className="mt-0.5" />
        <div>
          <div className="font-semibold">Couldn't load tonight's run</div>
          <div className="opacity-80">{error}</div>
          <button onClick={refresh}
                  data-testid="mce-runpanel-retry"
                  className="mt-2 text-[11px] underline">Retry</button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  const ps = preview.cooldown_status || {};
  const wp = preview.per_trigger_will_push || {};
  const cd = preview.per_trigger_cooldown || {};
  const dd = preview.per_trigger_deeply_dormant || {};
  const eligible = preview.per_trigger || {};
  const split = preview.priority_split || [];

  return (
    <div className="space-y-4" data-testid="mce-runpanel">

      {/* — Header — */}
      <div className="rounded border p-4"
            style={{ background: COLORS.primary, borderColor: COLORS.primary }}
            data-testid="mce-runpanel-header">
        <div className="flex items-center gap-3">
          <Heart size={18} style={{ color: COLORS.gold }} />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.22em] font-bold uppercase"
                  style={{ color: COLORS.gold }}>
              Member Care Engine
            </div>
            <div className="text-[18px] font-bold mt-0.5" style={{ color: '#fff' }}>
              Tonight's Run · {fmtDate(preview.anchor_date)}
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: '#A8B4C7' }}>
              {clientId.toUpperCase()} · fires at 03:00 IST · refreshes when you open this panel
            </div>
          </div>
          <button onClick={refresh} disabled={loading}
                  data-testid="mce-runpanel-refresh"
                  className="text-[11px] px-3 py-1.5 rounded border disabled:opacity-50 flex items-center gap-1"
                  style={{ background: 'rgba(255,255,255,0.08)', borderColor: '#A8B4C7', color: '#fff' }}>
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          <BigStat label="Total members" value={preview.total_members ?? '—'}
                    color="#fff" sub={clientId.toUpperCase()} />
          <BigStat label="Tonight's drafts"
                    value={preview.will_push ?? 0}
                    color="#fff" sub={`of ${preview.daily_cap ?? '—'} cap`} />
          <BigStat label="Deferred to tomorrow"
                    value={preview.capped_overflow ?? 0}
                    color={preview.capped_overflow > 0 ? '#FCA5A5' : '#A8B4C7'}
                    sub={preview.capped_overflow > 0 ? 'overflow' : 'within cap'} />
          <BigStat label="Cooldown skips"
                    value={(preview.skipped_cooldown ?? 0) + (preview.skipped_deeply_dormant ?? 0)}
                    color="#A8B4C7"
                    sub={`${preview.skipped_cooldown ?? 0} cool · ${preview.skipped_deeply_dormant ?? 0} deep`} />
        </div>
      </div>

      {/* — Trigger breakdown — */}
      <div className="rounded border" data-testid="mce-runpanel-triggers"
            style={{ background: COLORS.panelBg, borderColor: COLORS.panelBorder }}>
        <SectionHeader>Tonight's triggers</SectionHeader>
        <div className="p-3 space-y-2">
          {Object.entries(TRIGGER_GROUPS).map(([groupName, triggers]) => {
            const hasAny = triggers.some(t =>
              (eligible[t] || 0) + (cd[t] || 0) + (dd[t] || 0) + (wp[t] || 0) > 0
            );
            if (!hasAny) return null;
            return (
              <div key={groupName} className="space-y-1">
                <div className="text-[10px] tracking-[0.18em] font-bold uppercase mt-2"
                      style={{ color: COLORS.muted }}>
                  {groupName}
                </div>
                {triggers.map(t => {
                  const elig = eligible[t] || 0;
                  const cool = cd[t] || 0;
                  const deep = dd[t] || 0;
                  const will = wp[t] || 0;
                  if (elig + cool + deep + will === 0) return null;
                  return (
                    <TriggerRow key={t} trigger={t}
                                 eligible={elig}
                                 cooldown={cool}
                                 deeplyDormant={deep}
                                 willPush={will} />
                  );
                })}
              </div>
            );
          })}
          {Object.keys(eligible).length === 0 && (
            <div className="text-[12px] py-4 text-center" style={{ color: COLORS.muted }}>
              No triggers fire tonight. The queue is quiet.
            </div>
          )}
        </div>
      </div>

      {/* — Cap & priority split — */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded border" data-testid="mce-runpanel-cap"
              style={{ background: COLORS.panelBg, borderColor: COLORS.panelBorder }}>
          <SectionHeader>What gets sent tonight</SectionHeader>
          <div className="p-4 space-y-1.5">
            {split.length === 0 ? (
              <div className="text-[12px] py-3 text-center" style={{ color: COLORS.muted }}>
                Nothing pushes tonight.
              </div>
            ) : split.map((s, i) => (
              <PriorityRow key={s.lane} idx={i + 1} lane={s.lane} count={s.count} />
            ))}
            <div className="border-t pt-2 mt-2 flex items-center"
                  style={{ borderColor: COLORS.panelBorder }}>
              <div className="text-[11px] font-bold uppercase tracking-wide"
                    style={{ color: COLORS.ink }}>Total</div>
              <div className="ml-auto text-[14px] font-bold tabular-nums"
                    style={{ color: COLORS.green }}>{preview.will_push ?? 0}</div>
            </div>
            {preview.capped_overflow > 0 && (
              <div className="text-[11px] mt-1 italic" style={{ color: COLORS.amber }}>
                {preview.capped_overflow} deferred to tomorrow (cap = {preview.daily_cap})
              </div>
            )}
          </div>
        </div>

        {/* — Cooldown status — */}
        <div className="rounded border" data-testid="mce-runpanel-cooldown"
              style={{ background: COLORS.panelBg, borderColor: COLORS.panelBorder }}>
          <SectionHeader>Cooldown status</SectionHeader>
          <div className="p-4 space-y-2.5">
            <CooldownBar
              label="In 30-day quiet window"
              value={ps.in_quiet_window ?? 0}
              total={ps.total_members ?? 1}
              tone="amber"
              hint="recently nudged · won't get a follow-up tonight"
            />
            <CooldownBar
              label="Eligible for nudge"
              value={ps.eligible_for_nudge ?? 0}
              total={ps.total_members ?? 1}
              tone="green"
              hint="open to a dormant or Soul Form reminder"
            />
            <CooldownBar
              label="Deeply dormant (stopped)"
              value={ps.deeply_dormant ?? 0}
              total={ps.total_members ?? 1}
              tone="red"
              hint="3 attempts, no reply · only newsletter / festivals now"
            />
          </div>
        </div>
      </div>

      {/* — Last 7 days — */}
      <div className="rounded border" data-testid="mce-runpanel-recent"
            style={{ background: COLORS.panelBg, borderColor: COLORS.panelBorder }}>
        <SectionHeader>Last 7 days</SectionHeader>
        <div className="p-3 overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr style={{ color: COLORS.muted }}>
                <th className="text-left px-2 py-1.5 font-medium">Day</th>
                <th className="text-right px-2 py-1.5 font-medium">Sent</th>
                <th className="text-right px-2 py-1.5 font-medium">Replies</th>
                <th className="text-right px-2 py-1.5 font-medium">Response %</th>
              </tr>
            </thead>
            <tbody>
              {(recent?.rows || []).map(row => (
                <tr key={row.date} className="border-t"
                    style={{ borderColor: '#F0ECE6' }}
                    data-testid={`mce-recent-row-${row.date}`}>
                  <td className="px-2 py-1.5" style={{ color: COLORS.ink }}>
                    <span className="font-semibold">{row.weekday}</span>
                    <span className="ml-2 text-[10px]" style={{ color: COLORS.muted }}>
                      {row.date.slice(5)}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums"
                      style={{ color: COLORS.ink }}>{row.sent}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums"
                      style={{ color: row.replies > 0 ? COLORS.green : COLORS.muted }}>
                    {row.replies}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums"
                      style={{ color: row.response_rate_pct > 2 ? COLORS.green : COLORS.muted }}>
                    {row.response_rate_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] italic mt-2" style={{ color: COLORS.muted }}>
            "Replies" tracks Zoho ticket status moving off "Open" — proxy for LSM / member engagement.
            Detailed thread polling coming in v2.
          </div>
        </div>
      </div>

      {/* — Preview button — */}
      <div className="rounded border p-4"
            style={{ background: COLORS.goldBg, borderColor: COLORS.goldBorder }}>
        <div className="flex items-center gap-3">
          <Eye size={16} style={{ color: COLORS.amber }} />
          <div className="flex-1">
            <div className="text-[12px] font-semibold" style={{ color: COLORS.amber }}>
              Spot-check tonight's draft list
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: COLORS.muted }}>
              Open the full list — every member about to be drafted, sortable by trigger or name.
              Catch surprises before they ship.
            </div>
          </div>
          <button onClick={() => setShowList(true)}
                  data-testid="mce-runpanel-preview-btn"
                  className="text-[12px] px-3 py-1.5 rounded border font-semibold"
                  style={{ background: 'white', borderColor: COLORS.amber, color: COLORS.amber }}>
            Preview tonight's run →
          </button>
        </div>
      </div>

      {/* — Modal — */}
      {showList && (
        <PreviewListModal
          rows={preview.preview_list || []}
          clientId={clientId}
          anchorDate={preview.anchor_date}
          onSkippedChange={refresh}
          onClose={() => setShowList(false)} />
      )}
    </div>
  );
}


function BigStat({ label, value, color, sub }) {
  return (
    <div data-testid={`mce-bigstat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-[9.5px] tracking-[0.2em] font-bold uppercase opacity-70"
            style={{ color }}>{label}</div>
      <div className="text-[28px] font-bold tabular-nums leading-none mt-1"
            style={{ color }}>{value}</div>
      {sub && (
        <div className="text-[10px] mt-1 opacity-60" style={{ color }}>{sub}</div>
      )}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="px-4 py-2 border-b text-[10px] tracking-[0.18em] font-bold uppercase"
          style={{ background: COLORS.cream, borderColor: COLORS.panelBorder, color: COLORS.muted }}>
      {children}
    </div>
  );
}

function TriggerRow({ trigger, eligible, cooldown, deeplyDormant, willPush }) {
  const meta = TRIGGER_META[trigger] || { icon: '•', label: trigger };
  const totalSuppressed = cooldown + deeplyDormant;
  return (
    <div className="flex items-start py-1.5 px-2 rounded hover:bg-amber-50/30"
          data-testid={`mce-trigger-${trigger}`}>
      <div className="flex-1 flex items-center gap-2">
        <span className="text-[15px]" style={{ width: 22 }}>{meta.icon}</span>
        <span className="text-[12px]" style={{ color: COLORS.ink }}>{meta.label}</span>
      </div>
      <div className="text-[11.5px] tabular-nums flex items-center gap-3"
            style={{ color: COLORS.muted }}>
        <span>{eligible} eligible</span>
        {cooldown > 0 && (
          <span style={{ color: COLORS.amber }}>−{cooldown} cooldown</span>
        )}
        {deeplyDormant > 0 && (
          <span style={{ color: COLORS.red }}>−{deeplyDormant} deep</span>
        )}
        <span className="font-bold" style={{ color: COLORS.green, minWidth: 60, textAlign: 'right' }}>
          → {willPush} tonight
        </span>
      </div>
    </div>
  );
}

function PriorityRow({ idx, lane, count }) {
  return (
    <div className="flex items-center text-[12px]"
          data-testid={`mce-priority-${idx}`}>
      <span className="text-[10px] font-bold mr-2 px-1.5 rounded"
            style={{ background: COLORS.cream, color: COLORS.muted }}>
        P{idx}
      </span>
      <span style={{ color: COLORS.ink }}>{lane}</span>
      <span className="ml-auto font-semibold tabular-nums"
            style={{ color: COLORS.ink }}>{count}</span>
    </div>
  );
}

function CooldownBar({ label, value, total, tone, hint }) {
  const pct = total > 0 ? Math.round(100 * value / total) : 0;
  const palette = tone === 'amber'
    ? { fg: COLORS.amber, bg: COLORS.amberBg }
    : tone === 'red'
    ? { fg: COLORS.red, bg: COLORS.redBg }
    : { fg: COLORS.green, bg: COLORS.greenBg };
  return (
    <div data-testid={`mce-cooldown-${tone}`}>
      <div className="flex items-center justify-between text-[11.5px]">
        <span style={{ color: COLORS.ink }}>{label}</span>
        <span className="tabular-nums font-semibold" style={{ color: palette.fg }}>
          {value.toLocaleString('en-IN')} <span className="opacity-60 font-normal">({pct}%)</span>
        </span>
      </div>
      <div className="h-1.5 rounded mt-1 overflow-hidden"
            style={{ background: palette.bg }}>
        <div className="h-full rounded transition-all"
              style={{ width: `${pct}%`, background: palette.fg }} />
      </div>
      {hint && (
        <div className="text-[10px] mt-1 italic" style={{ color: COLORS.muted }}>{hint}</div>
      )}
    </div>
  );
}


function PreviewListModal({ rows, onClose, clientId, anchorDate, onSkippedChange }) {
  const [sortKey, setSortKey] = useState('trigger');
  const [filter, setFilter]   = useState('');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [skipped, setSkipped] = useState({});      // key: `${member_id}|${trigger}` → {reason, skipped_by}
  const [busyKey, setBusyKey] = useState(null);
  const [skipDialog, setSkipDialog] = useState(null);  // {row, reason}

  // Refresh-safe: pull skips from DB on mount so badges survive reload.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/member-care/${clientId}/skipped-drafts`,
                                    { params: { anchor_date: anchorDate } });
        if (!alive) return;
        const map = {};
        for (const r of (res.data?.rows || [])) {
          map[`${r.member_id}|${r.trigger}`] = {
            reason: r.skip_reason, by: r.skipped_by,
          };
        }
        setSkipped(map);
      } catch { /* non-fatal — modal still works without badges */ }
    })();
    return () => { alive = false; };
  }, [clientId, anchorDate]);

  const skipRow = async (row) => {
    const key = `${row.member_id}|${row.trigger}`;
    setBusyKey(key);
    try {
      await api.post(`/member-care/${clientId}/skip-draft`, {
        member_id: row.member_id,
        trigger: row.trigger,
        anchor_date: anchorDate,
        reason: skipDialog?.reason || null,
      });
      setSkipped(prev => ({ ...prev, [key]: {
        reason: skipDialog?.reason || null, by: 'you',
      }}));
      setSkipDialog(null);
      onSkippedChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Skip failed');
    } finally { setBusyKey(null); }
  };

  const unskipRow = async (row) => {
    const key = `${row.member_id}|${row.trigger}`;
    if (!confirm(`Re-enable tonight's draft for ${row.member_name}?`)) return;
    setBusyKey(key);
    try {
      await api.post(`/member-care/${clientId}/unskip-draft`, {
        member_id: row.member_id,
        trigger: row.trigger,
        anchor_date: anchorDate,
      });
      setSkipped(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      onSkippedChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Unskip failed');
    } finally { setBusyKey(null); }
  };

  const triggers = useMemo(() =>
    Array.from(new Set(rows.map(r => r.trigger))).sort()
  , [rows]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows
      .filter(r => triggerFilter === 'all' || r.trigger === triggerFilter)
      .filter(r => !f || (r.member_name || '').toLowerCase().includes(f))
      .sort((a, b) => {
        if (sortKey === 'trigger') {
          return (a.trigger || '').localeCompare(b.trigger || '')
                  || (a.member_name || '').localeCompare(b.member_name || '');
        }
        if (sortKey === 'urgency') {
          const order = { high: 0, normal: 1, low: 2 };
          return (order[a.urgency] ?? 9) - (order[b.urgency] ?? 9)
                  || (a.member_name || '').localeCompare(b.member_name || '');
        }
        return (a.member_name || '').localeCompare(b.member_name || '');
      });
  }, [rows, filter, sortKey, triggerFilter]);

  const skippedCount = Object.keys(skipped).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(11, 17, 32, 0.55)' }}
          data-testid="mce-preview-modal">
      <div className="w-full max-w-4xl max-h-[88vh] flex flex-col rounded-lg border overflow-hidden"
            style={{ background: 'white', borderColor: COLORS.panelBorder }}>
        <div className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ background: COLORS.cream, borderColor: COLORS.panelBorder }}>
          <Eye size={14} style={{ color: COLORS.gold }} />
          <div className="flex-1">
            <div className="text-[14px] font-semibold" style={{ color: COLORS.ink }}>
              Tonight's draft list · {filtered.length} of {rows.length} members
              {skippedCount > 0 && (
                <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded"
                      style={{ background: COLORS.redBg, color: COLORS.red }}
                      data-testid="mce-preview-skipped-count">
                  {skippedCount} skipped
                </span>
              )}
            </div>
            <div className="text-[11px]" style={{ color: COLORS.muted }}>
              Spot-check before the cron fires. Click <strong>Skip</strong> on any row to
              kill that draft for tonight only.
            </div>
          </div>
          <button onClick={onClose}
                  data-testid="mce-preview-modal-close"
                  className="p-1.5 rounded hover:bg-amber-50">
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-2 border-b flex items-center gap-2"
              style={{ borderColor: COLORS.panelBorder, background: 'white' }}>
          <input
            type="text"
            placeholder="Search member name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="mce-preview-search"
            className="flex-1 text-[12px] px-3 py-1.5 rounded border"
            style={{ borderColor: COLORS.panelBorder }}
          />
          <select
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value)}
            data-testid="mce-preview-trigger-filter"
            className="text-[11.5px] px-2 py-1.5 rounded border bg-white"
            style={{ borderColor: COLORS.panelBorder }}>
            <option value="all">All triggers</option>
            {triggers.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="mce-preview-sort"
            className="text-[11.5px] px-2 py-1.5 rounded border bg-white"
            style={{ borderColor: COLORS.panelBorder }}>
            <option value="trigger">Sort: Trigger</option>
            <option value="urgency">Sort: Urgency</option>
            <option value="name">Sort: Name</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-[12px]" style={{ color: COLORS.muted }}>
              No members match these filters.
            </div>
          ) : (
            <table className="w-full text-[11.5px]">
              <thead className="sticky top-0"
                      style={{ background: COLORS.cream, color: COLORS.muted }}>
                <tr>
                  <th className="text-left px-3 py-2 font-medium">#</th>
                  <th className="text-left px-3 py-2 font-medium">Member</th>
                  <th className="text-left px-3 py-2 font-medium">Trigger</th>
                  <th className="text-left px-3 py-2 font-medium">Urgency</th>
                  <th className="text-left px-3 py-2 font-medium">Attempt</th>
                  <th className="text-right px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const meta = TRIGGER_META[r.trigger] || { icon: '•', label: r.trigger };
                  const key = `${r.member_id}|${r.trigger}`;
                  const isSkipped = !!skipped[key];
                  const isBusy = busyKey === key;
                  return (
                    <tr key={`${key}-${i}`} className="border-t"
                        style={{
                          borderColor: '#F0ECE6',
                          background: isSkipped ? '#FEF2F2' : 'transparent',
                          opacity: isSkipped ? 0.65 : 1,
                        }}
                        data-testid={`mce-preview-row-${i}`}>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: COLORS.muted }}>
                        {i + 1}
                      </td>
                      <td className="px-3 py-1.5" style={{ color: COLORS.ink }}>
                        {r.member_name || '—'}
                        {isSkipped && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold tracking-wide uppercase"
                                style={{ background: COLORS.redBg, color: COLORS.red }}>
                            🚫 Skipped
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="mr-1.5">{meta.icon}</span>
                        <span className="text-[10.5px]" style={{ color: COLORS.muted }}>
                          {r.trigger}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-[10.5px]"
                          style={{ color: r.urgency === 'high' ? COLORS.red : COLORS.muted }}>
                        {r.urgency || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-[10.5px]" style={{ color: COLORS.muted }}>
                        {r.attempt_number ? `${r.attempt_number} of 3` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {isSkipped ? (
                          <button
                            onClick={() => unskipRow(r)}
                            disabled={isBusy}
                            data-testid={`mce-unskip-btn-${i}`}
                            className="text-[10.5px] px-2 py-0.5 rounded border disabled:opacity-50 hover:bg-emerald-50"
                            style={{ borderColor: COLORS.green, color: COLORS.green, background: 'white' }}>
                            {isBusy ? '…' : 'Re-enable'}
                          </button>
                        ) : (
                          <button
                            onClick={() => setSkipDialog({ row: r, reason: '' })}
                            disabled={isBusy}
                            data-testid={`mce-skip-btn-${i}`}
                            className="text-[10.5px] px-2 py-0.5 rounded border disabled:opacity-50 hover:bg-red-50"
                            style={{ borderColor: COLORS.red, color: COLORS.red, background: 'white' }}>
                            {isBusy ? '…' : 'Skip'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-2 border-t text-[10px] italic"
              style={{ background: COLORS.cream, borderColor: COLORS.panelBorder, color: COLORS.muted }}>
          Read-only preview · re-sortable · drafts only push when the 03:00 IST cron fires (or via "Run live now").
          Skipped rows are tonight-only — they'll be eligible again tomorrow.
        </div>
      </div>

      {/* Skip-with-reason confirm dialog */}
      {skipDialog && (
        <div className="absolute inset-0 flex items-center justify-center p-6"
              style={{ background: 'rgba(11, 17, 32, 0.7)' }}
              data-testid="mce-skip-dialog">
          <div className="w-full max-w-md rounded-lg border overflow-hidden shadow-xl"
                style={{ background: 'white', borderColor: COLORS.panelBorder }}>
            <div className="px-4 py-3 border-b flex items-center gap-2"
                  style={{ background: COLORS.redBg, borderColor: COLORS.red }}>
              <AlertCircle size={14} style={{ color: COLORS.red }} />
              <div className="text-[13px] font-semibold" style={{ color: COLORS.red }}>
                Skip tonight's draft for {skipDialog.row.member_name}?
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[12px]" style={{ color: COLORS.ink }}>
                This kills the <code className="px-1 rounded text-[11px]"
                  style={{ background: COLORS.cream }}>{skipDialog.row.trigger}</code>
                {' '}draft for tonight only. They'll be eligible again tomorrow.
              </div>
              <div>
                <label className="text-[10.5px] tracking-[0.18em] font-bold uppercase block mb-1"
                        style={{ color: COLORS.muted }}>
                  Reason (optional, for audit log)
                </label>
                <textarea
                  rows={2}
                  value={skipDialog.reason}
                  onChange={(e) => setSkipDialog(prev => ({ ...prev, reason: e.target.value }))}
                  data-testid="mce-skip-reason"
                  placeholder="e.g. just complained · do-not-disturb · bereavement"
                  className="w-full text-[12px] px-3 py-2 rounded border"
                  style={{ borderColor: COLORS.panelBorder }}
                />
              </div>
              <div className="flex items-center gap-2 justify-end pt-1">
                <button onClick={() => setSkipDialog(null)}
                        data-testid="mce-skip-dialog-cancel"
                        className="text-[12px] px-3 py-1.5 rounded border"
                        style={{ borderColor: COLORS.panelBorder, color: COLORS.muted, background: 'white' }}>
                  Cancel
                </button>
                <button onClick={() => skipRow(skipDialog.row)}
                        data-testid="mce-skip-dialog-confirm"
                        className="text-[12px] px-3 py-1.5 rounded font-semibold"
                        style={{ background: COLORS.red, color: 'white' }}>
                  Skip this draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
